import * as React from 'react'
import { TreeManager } from '@basehub/utils/tree/index'
import {
  Block,
  CommentBlock,
  isBlockWithChildren,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'

export const useCommentKeyboardNavigationHelpers = ({
  block,
  blockTreeManagerRef,
}: {
  block: CommentBlock
  blockTreeManagerRef: React.MutableRefObject<
    TreeManager<Block, NonNullable<unknown>> | null | undefined
  >
}) => {
  const getAdjacentBlocks = React.useCallback(() => {
    invariant(blockTreeManagerRef.current, 'Block tree manager should be found')

    const blockNodeInWorkingTreeManager = blockTreeManagerRef.current.getNode(
      block.id
    )
    invariant(
      blockNodeInWorkingTreeManager,
      `${block.type} block with id ${block.id} was not found`
    )

    // Does not have parent, return null
    if (!blockNodeInWorkingTreeManager.parentKey) {
      return { prevBlock: null, currentBlock: block, block: null }
    }

    const parentBlockNodeInWorkingTreeManager =
      blockTreeManagerRef.current.getNode(
        blockNodeInWorkingTreeManager.parentKey
      )
    invariant(
      parentBlockNodeInWorkingTreeManager,
      `parent block of ${blockNodeInWorkingTreeManager?.value.type} block with id ${blockNodeInWorkingTreeManager?.parentKey} was not found`
    )
    invariant(
      isBlockWithChildren(parentBlockNodeInWorkingTreeManager.value),
      `This is a bug in basehub, <parentBlockNodeInWorkingTreeManager> should be block with children, but found ${parentBlockNodeInWorkingTreeManager?.value.type}`
    )

    const childrenKeys = blockTreeManagerRef.current.getChildrenKeys(
      parentBlockNodeInWorkingTreeManager.key
    )
    const currentBlockIndex = childrenKeys.findIndex((cId) => cId === block.id)
    const currentBlock = childrenKeys[currentBlockIndex]

    invariant(currentBlock, 'Current block should be found')

    const prevBlockInFlatTree = childrenKeys[currentBlockIndex - 1]
    const nextBlockInFlatTree = childrenKeys[currentBlockIndex + 1]

    const prevSibling = prevBlockInFlatTree ?? null
    const nextSibling = nextBlockInFlatTree ?? null

    return {
      parentBlock: parentBlockNodeInWorkingTreeManager.value,
      prevBlock: prevSibling
        ? blockTreeManagerRef.current.getNode(prevSibling)
        : null,
      currentBlock,
      nextBlock: nextSibling
        ? blockTreeManagerRef.current.getNode(nextSibling)
        : null,
    }
  }, [block, blockTreeManagerRef])

  return { getAdjacentBlocks }
}
